import React, { useState, useEffect } from 'react';
import { HiOutlinePencilSquare } from 'react-icons/hi2';
import Modal from 'react-modal';
import MDEditor from '@uiw/react-md-editor';

// Bind modal to your app element (for accessibility)
Modal.setAppElement('#root');

function Notes({ onDataChange }) {
  const [subject, setSubject] = useState('Default Subject');
  const [recipient, setRecipient] = useState('Default Recipient');
  const [tags, setTags] = useState([]);
  const [buttonClickCount, setButtonClickCount] = useState({
    tryIn: 0,
    emerg: 0,
    nextCase: 0,
    pickup: 0,
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [editorValue, setEditorValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const formData = {
      subject,
      recipient,
      tags,
      buttonClickCount,
      attachedFiles,
      editorHtml: editorValue, // MDEditor returns markdown content
    };
    if (onDataChange) {
      onDataChange(formData);
    }
  }, [subject, recipient, tags, buttonClickCount, attachedFiles, editorValue, onDataChange]);

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
  };

  const handleRecipientChange = (event) => {
    setRecipient(event.target.value);
  };

  const handleButtonClick = (buttonName) => {
    setButtonClickCount((prevState) => ({
      ...prevState,
      [buttonName]: prevState[buttonName] + 1,
    }));
  };

  const isButtonGreen = (buttonName) => buttonClickCount[buttonName] % 2 === 1;

  const handleFileChange = (event) => {
    const files = event.target.files;
    setAttachedFiles((prevAttachedFiles) => [
      ...prevAttachedFiles,
      ...Array.from(files),
    ]);
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prevAttachedFiles) => {
      const newAttachedFiles = [...prevAttachedFiles];
      newAttachedFiles.splice(index, 1);
      return newAttachedFiles;
    });
  };

  const handleAddTag = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setTags((prevTags) => {
        const newTag = event.target.value.trim();
        if (!prevTags.includes(newTag)) {
          return [...prevTags, newTag];
        }
        return prevTags;
      });
      event.target.value = '';
    }
  };

  const handleRemoveTag = (index) => {
    setTags((prevTags) => {
      const newTags = [...prevTags];
      newTags.splice(index, 1);
      return newTags;
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditorValue('');
    setSubject('Default Subject');
    setRecipient('Default Recipient');
  };

  const saveNote = () => {
    if (!editorValue.trim()) {
      alert('Note cannot be empty');
      return;
    }

    // MDEditor stores content as markdown, convert to HTML for display
    const formattedNote = `
      <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 5px;">
        ${recipient} (for internal use only)<br/>
        Created: ${new Date().toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })} for ${subject}
      </div>
      <div style="padding: 10px;">
        ${editorValue}
      </div>
    `;

    // Update the editor value with the formatted note
    // If you need to store as markdown, keep as is
    setEditorValue(formattedNote);
    setIsModalOpen(false);
  };

  // Styles
  const mainContainerStyle = {
    backgroundColor: 'white',
    padding: '10px',
  };

  const titleStyle = {
    backgroundColor: 'white',
    color: '#004aad',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  };

  const formContainerStyle = {
    border: '1px solid #ccc',
    padding: '20px',
    borderTop: '3px solid #004aad',
    backgroundColor: '#f4f4f4',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  };

  const columnStyle = {
    flex: '1',
    minWidth: '300px',
  };

  const selectContainerStyle = {
    display: 'flex',
    gap: '5px',
    marginBottom: '15px',
  };

  const selectStyle = {
    borderRadius: '4px',
    fontSize: '19px',
    padding: '5px',
    border: '1px solid #ccc',
  };

  const buttonStyle = {
    marginRight: '4px',
    borderRadius: '4px',
    padding: '5px 10px',
    border: '1px solid #ccc',
    cursor: 'pointer',
    marginBottom: '10px',
  };

  const fileInputStyle = {
    marginBottom: '15px',
  };

  const fileListStyle = {
    listStyle: 'none',
    padding: '0',
  };

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  };

  const removeFileButtonStyle = {
    marginLeft: '10px',
    color: 'red',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  };

  const tagInputStyle = {
    marginLeft: '10px',
    borderRadius: '4px',
    padding: '5px',
    border: '1px solid #ccc',
    marginBottom: '10px',
  };

  const tagContainerStyle = {
    marginTop: '10px',
  };

  const tagItemStyle = {
    margin: '5px',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const tagStyle = {
    padding: '5px 10px',
    backgroundColor: '#f1f1f1',
    borderRadius: '20px',
    border: '1px solid #ccc',
  };

  const removeTagButtonStyle = {
    marginLeft: '5px',
    color: 'red',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={mainContainerStyle}>
      <h3 style={titleStyle}>
        <HiOutlinePencilSquare style={{ marginRight: '5px', color: '#004aad' }} />
        Notes
      </h3>
      <div style={formContainerStyle}>
        <div style={columnStyle}>
          <button
            onClick={openModal}
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '8px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Add Note
          </button>
        </div>

        <div style={columnStyle}>
          {/* Buttons for handling actions */}
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isButtonGreen('tryIn') ? 'green' : 'initial',
              color: isButtonGreen('tryIn') ? 'white' : 'inherit',
            }}
            onClick={() => handleButtonClick('tryIn')}
          >
            Try-In
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isButtonGreen('emerg') ? 'green' : 'initial',
              color: isButtonGreen('emerg') ? 'white' : 'inherit',
            }}
            onClick={() => handleButtonClick('emerg')}
          >
            Emerg
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isButtonGreen('nextCase') ? 'green' : 'initial',
              color: isButtonGreen('nextCase') ? 'white' : 'inherit',
            }}
            onClick={() => handleButtonClick('nextCase')}
          >
            Next-case
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isButtonGreen('pickup') ? 'green' : 'initial',
              color: isButtonGreen('pickup') ? 'white' : 'inherit',
            }}
            onClick={() => handleButtonClick('pickup')}
          >
            Pickup
          </button>

          {/* File Upload Input */}
          <div style={fileInputStyle}>
            <input type="file" multiple onChange={handleFileChange} />
          </div>

          {/* Displaying Attached Files */}
          {attachedFiles.length > 0 && (
            <div>
              <p>Attached Files:</p>
              <ul style={fileListStyle}>
                {attachedFiles.map((file, index) => (
                  <li key={index} style={fileItemStyle}>
                    {file.name}
                    <button
                      style={removeFileButtonStyle}
                      onClick={() => handleRemoveFile(index)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tags Section */}
          <div style={{ marginTop: '30px', marginBottom: '30px' }}>
            <div>
              <input
                type="text"
                onKeyDown={handleAddTag}
                style={tagInputStyle}
                placeholder="Enter tag and press Enter"
              />
            </div>

            <div style={tagContainerStyle}>
              {tags.length > 0 && (
                <div>
                  <p>Selected Tags:</p>
                  <div>
                    {tags.map((tag, index) => (
                      <span key={index} style={tagItemStyle}>
                        <span style={tagStyle}>{tag}</span>
                        <button
                          onClick={() => handleRemoveTag(index)}
                          style={removeTagButtonStyle}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Rich Text Editor */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '800px',
            maxWidth: '90%',
            padding: '20px',
            borderRadius: '8px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          },
        }}
        contentLabel="Add Rich Note Modal"
      >
        <h2 style={{ color: '#004aad', marginBottom: '20px' }}>Add Note</h2>
        <div style={{ marginBottom: '20px' }}>
          <div style={selectContainerStyle}>
            <select
              value={subject}
              onChange={handleSubjectChange}
              style={selectStyle}
            >
              <option value="Default Subject">Select Subject</option>
              <option value="case shipping">Case Shipping</option>
              <option value="case fabrication">Case Fabrication</option>
              <option value="case invoice">Case Invoice</option>
            </select>
            <select
              value={recipient}
              onChange={handleRecipientChange}
              style={selectStyle}
            >
              <option value="Default Recipient">Select Recipient</option>
              <option value="accounts">Accounts</option>
              <option value="lab">Lab</option>
              <option value="admin SM">Admin SM</option>
              <option value="admin SE">Admin SE</option>
            </select>
          </div>
          <div
            style={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
              minHeight: '300px',
            }}
          >
            <MDEditor
              value={editorValue}
              onChange={setEditorValue}
              height={250}
              preview="edit"
              placeholder="Start typing your notes..."
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={closeModal}
            style={{
              backgroundColor: '#f0f0f0',
              color: '#333',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={saveNote}
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            disabled={!editorValue.trim()}
          >
            Save Note
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Notes;